import React, {useEffect, useState} from 'react';
import authService from '../../Auth/AuthService';
import {Box, Button, FormLabel, TextField, Card, CardContent, Typography, Container, Alert, CircularProgress} from '@mui/material';
import PropTypes from 'prop-types';
import authenticationManager from '../../Auth/AuthenticationManager';
import {useNavigate, useSearchParams} from 'react-router-dom';
import {getSafeReturnUrl} from '../../Auth/loginRedirect';
import PasswordResetModal from '../Modal/StandardFunctions/PasswordResetModal';
import {Login as LoginIcon, Lock as LockIcon, Email as EmailIcon} from '@mui/icons-material';

const Login = (props) => {
    const { isMobile, setSnackbarProperties } = props;
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [usernameError, setUsernameError] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [loggingIn, setLoggingIn] = useState(false);
    const [resettingPassword, setResettingPassword] = useState(false);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    async function handleSubmit () {
        setLoggingIn(true);
        if (validate()) {
            const result = await authService.login(username, password);
            if (result.data.success) {
                redirectToUserInformationPage();
            } else {
                setPassword('');
                setLoginError(result.data.message);
            }
        }

        setLoggingIn(false);
    }

    function redirectToUserInformationPage () {
        const returnUrl = getSafeReturnUrl(searchParams.get('returnUrl'));
        navigate(returnUrl ?? '/user-info', { replace: true });
    }

    async function handleKeyPress(event) {
        if(event.key === 'Enter') {
            event.preventDefault();
            await handleSubmit();
        }
    }

    function validate () {
        if (username === '') {
            setUsernameError('Username must be entered');
        } else {
            setUsernameError('');
        }
        if (password === '') {
            setPasswordError('Password must be entered');
        } else {
            setPasswordError('');
        }

        if (usernameError || passwordError) {
            setLoginError('');
            return false;
        }

        return true;
    }

    function handleResetPasswordButton() {
        setShowPasswordResetModal(true);
    }

    function closeResetModal() {
        setShowPasswordResetModal(false);
    }

    async function handleResetPasswordRequest(email) {
        setResettingPassword(true);
        const response = await authService.requestResetPassword(email);

        if(response.success) {
            setSnackbarProperties(response.message);
        } else if(response.message){
            setSnackbarProperties(response.message, 'error');
        } else {
            setSnackbarProperties(response, 'error');
        }
        setResettingPassword(false);
        closeResetModal();
    }

    useEffect(() => {
        if(authenticationManager.isAuthorized) {
            navigate('/user-info');
        }
        document.title = 'Executive Storage Suites - Login';
    }, [isMobile]);

    return (
        <Box 
            sx={{ 
                minHeight: '50vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 2
            }}
        >
            <Container maxWidth="sm">
                <Card 
                    elevation={8}
                    sx={{
                        borderRadius: 4,
                        overflow: 'hidden',
                        backgroundColor: 'white'
                    }}
                >
                    <CardContent sx={{ padding: { xs: 3, sm: 4, md: 5 } }}>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Box 
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #007A03 0%, #005502 100%)',
                                    mb: 3,
                                    boxShadow: '0 8px 32px rgba(0, 122, 3, 0.3)'
                                }}
                            >
                                <LoginIcon sx={{ fontSize: 40, color: 'white' }} />
                            </Box>
                            <Typography 
                                variant="h3" 
                                component="h1" 
                                sx={{ 
                                    fontWeight: 700,
                                    color: '#2c3e50',
                                    mb: 1,
                                    fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' }
                                }}
                            >
                                Welcome Back
                            </Typography>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: '#7f8c8d',
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                    lineHeight: 1.6
                                }}
                            >
                                Sign in to access your storage units and manage your account
                            </Typography>
                        </Box>

                        {loginError && (
                            <Alert 
                                severity="error" 
                                sx={{ 
                                    mb: 3,
                                    borderRadius: 2,
                                    '& .MuiAlert-message': {
                                        fontSize: '0.95rem'
                                    }
                                }}
                            >
                                {loginError}
                            </Alert>
                        )}

                        <Box component="form" sx={{ mt: 2 }}>
                            <Box sx={{ mb: 3 }}>
                                <FormLabel 
                                    sx={{ 
                                        fontWeight: 600,
                                        color: '#2c3e50',
                                        mb: 1,
                                        display: 'block',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    Email Address
                                </FormLabel>
                                <TextField
                                    fullWidth
                                    type="email"
                                    autoComplete="username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    error={!!usernameError}
                                    helperText={usernameError}
                                    placeholder="Enter your email address"
                                    InputProps={{
                                        startAdornment: (
                                            <EmailIcon sx={{ color: '#7f8c8d', mr: 1 }} />
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: '#f8f9fa',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: '#ffffff',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#007A03',
                                                }
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: '#ffffff',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#007A03',
                                                    borderWidth: 2
                                                }
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            padding: '16px 14px',
                                            fontSize: '1rem'
                                        }
                                    }}
                                />
                            </Box>

                            <Box sx={{ mb: 4 }}>
                                <FormLabel 
                                    sx={{ 
                                        fontWeight: 600,
                                        color: '#2c3e50',
                                        mb: 1,
                                        display: 'block',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    Password
                                </FormLabel>
                                <TextField
                                    fullWidth
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    error={!!passwordError}
                                    helperText={passwordError}
                                    placeholder="Enter your password"
                                    InputProps={{
                                        startAdornment: (
                                            <LockIcon sx={{ color: '#7f8c8d', mr: 1 }} />
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: '#f8f9fa',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                backgroundColor: '#ffffff',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#007A03',
                                                }
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: '#ffffff',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#007A03',
                                                    borderWidth: 2
                                                }
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            padding: '16px 14px',
                                            fontSize: '1rem'
                                        }
                                    }}
                                />
                            </Box>

                            <Button
                                fullWidth
                                size="large"
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={loggingIn}
                                startIcon={loggingIn ? <CircularProgress size={20} color="inherit" /> : null}
                                sx={{
                                    mb: 3,
                                    py: 1.8,
                                    borderRadius: 2,
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    background: 'linear-gradient(135deg, #007A03 0%, #005502 100%)',
                                    boxShadow: '0 8px 32px rgba(0, 122, 3, 0.3)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #005502 0%, #007A03 100%)',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 12px 48px rgba(0, 122, 3, 0.4)'
                                    },
                                    '&:disabled': {
                                        background: 'linear-gradient(135deg, #cccccc 0%, #999999 100%)',
                                        transform: 'none',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {loggingIn ? 'Signing In...' : 'Sign In'}
                            </Button>

                            <Button
                                fullWidth
                                variant="text"
                                onClick={handleResetPasswordButton}
                                disabled={loggingIn}
                                sx={{
                                    py: 1.5,
                                    fontSize: '0.95rem',
                                    fontWeight: 500,
                                    textTransform: 'none',
                                    color: '#007A03',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 122, 3, 0.04)',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            >
                                Forgot your password?
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
            
            <PasswordResetModal
                open={showPasswordResetModal}
                close={closeResetModal}
                handleSubmitReset={handleResetPasswordRequest}
                loading={resettingPassword}
            />
        </Box>
    );
};

Login.propTypes = {
    isMobile: PropTypes.bool,
    setSnackbarProperties: PropTypes.func,
};
export default Login;
